import { reactive } from 'vue'
import store from '@/store/index'
import type { StoredColumnWidth } from '@/store/V2Reservation/V2ReservationCellConfig'

/*
 * 담당자 열 너비 — 구 ColumnWidthController 를 모듈로 옮겼다 (MDP-2295 ⑥ 1단계).
 *
 * 헤더 오른쪽 끝의 손잡이를 끄는 동안의 상태를 들고 있다. 끄는 중에는 가이드선만 움직이고,
 * 놓는 순간 한 번에 너비를 확정해 vuex 에 넣는다(vuex-persistedstate 가 localStorage 에 얹는다).
 * 서버는 부르지 않는다 — 너비는 이 브라우저의 기록이다.
 *
 * 구 컨트롤러와 달라진 점 하나: 화면이 전역 인스턴스에 자기 물건(areaHelper, groups)을
 * 꽂아 두던 것을 끊고, 끌기를 시작할 때 인자로 받는다. 어차피 두 호출처 모두
 * mousedown 직전에 매번 새로 꽂고 있어서 실질은 같고, 남의 물건이 전역에 눌러앉지 않는다.
 *
 * 함께 지운 것: startColumnID / targetColumn (대입만 하고 읽는 곳 없음), handleCancel
 * (호출처 없음), defaultColumnWidth (읽는 곳 없음).
 */

type Column = { _id: string; isSelected?: boolean }
type Group = { columns: Column[] }

/** 시트가 들고 있는 선택 영역 헬퍼 (V2ReservationSelectAreaHelper) 중 여기서 쓰는 부분만 */
type AreaHelper = {
    isColumnGroupSelection: boolean
    selectedRange: { start: { x?: number }; end: { x?: number } }
}


const currentHospitalID = (): string | undefined => store.state.hospital?.selectedHospital?._id

/** 저장된 너비가 없는 열의 기본값. vuex 의 defaultWidth 와 같은 값을 구 컨트롤러가 상수로 박아 두었다 */
const FALLBACK_WIDTH = 160

const storedWidthsOf = (): StoredColumnWidth[] =>
    store.state.V2ReservationCellConfig.width[currentHospitalID() ?? ''] ?? []

export const columnWidth = reactive({
    /** 손잡이를 잡고 있는 동안 참. 시트·헤더는 이때 다른 마우스 처리를 하지 않는다 */
    isResizing: false,
    /** 가이드선의 x 좌표 (pageX) */
    guideX: 0,
    /**
     * 저장된 너비 목록. 헤더·열·현재시각선이 이걸 읽어 자기 너비를 정한다.
     *
     * 이 모듈이 처음 불릴 때 한 번 읽고, 이후로는 끌기를 놓을 때만 갱신한다. 병원이
     * 바뀌어도 다시 읽지 않는다 — 구 컨트롤러가 생성자에서 하던 그대로다.
     *
     * 그래도 두는 이유 (MDP-2295 ⑥ 에서 확인):
     *   - 예약 창이 새로고침 없이 병원을 갈아타는 경로는 "다른 병원 푸시 알림 클릭" 하나뿐이고
     *     (AppController.pushMessageClickHandler → Application.Set.hospitalID 브로드캐스트),
     *     MSO 계정(userInfo.type 2501)은 그때 서브 윈도우가 전부 닫혀 예약 창도 같이 닫힌다.
     *     즉 다시 열리면서 새로 읽는다.
     *   - 남는 경우는 MSO 가 아닌 다중 병원 계정인데, 내부 계정 말고는 없다.
     *   - 반대로 store 에서 매번 읽도록 바꾸면 Vue 2 의 새 키 반응성에 걸린다.
     *     MU_V2ReservationCellWidth 가 첫 병원만 객체를 통째로 갈아끼우고 둘째 병원부터는
     *     `state.width[hospitalID] = ...` 로 키를 더하기 때문에, 둘째 병원부터 화면이
     *     갱신되지 않을 수 있다. 도달하지 않는 문제를 고치려다 도달하는 문제를 만드는 셈이다.
     */
    storedWidths: storedWidthsOf() as StoredColumnWidth[],
})

// 끄는 동안에만 쓰는 값 — 화면이 보지 않으므로 반응형 밖에 둔다
let targetColumnID: string | undefined
let targetColumnIDs: string[] = []
let startPositionX = 0

/** 필터에서 켜져 있는 열을 그룹 순서대로 펼친 목록. selectedRange 의 x 는 이 목록의 인덱스다 */
function visibleColumns(groups: Group[]): Column[] {
    let columns: Column[] = []
    for (let i = 0; i < groups.length; i++) {
        columns = columns.concat(groups[i].columns.filter((c) => c.isSelected))
    }
    return columns
}

/**
 * 이번 끌기로 너비가 바뀔 열들.
 *
 * 헤더를 가로로 끌어 범위를 잡아 두었다면 그 범위 전체가 대상이고, 아니면 잡은 열 하나뿐이다.
 * 범위가 비어 있으면(선택한 적 없음) start.x 가 undefined 라 아래 반복문은 돌지 않는다.
 */
function resizeTargets(columnID: string, areaHelper: AreaHelper, groups: Group[]): string[] {
    const ids: string[] = []

    if (areaHelper.isColumnGroupSelection == true) {
        const columns = visibleColumns(groups)
        let startX = areaHelper.selectedRange.start.x
        let endX = areaHelper.selectedRange.end.x
        if (typeof startX === 'number' && typeof endX === 'number') {
            if (endX < startX) {
                const temp = endX
                endX = startX
                startX = temp
            }
            for (let i = startX; i <= endX; i++) {
                const value = columns[i]
                if (value == undefined) { continue }
                ids.push(value._id)
            }
        }
    }
    if (ids.includes(columnID) == false) { ids.push(columnID) }

    return ids
}

const widthOf = (columnID: string | undefined): number =>
    columnWidth.storedWidths.find((w) => w._id == columnID)?.width ?? FALLBACK_WIDTH

export function startColumnResize(
    e: MouseEvent,
    columnID: string,
    context: { areaHelper: AreaHelper; groups: Group[] },
): void {
    columnWidth.isResizing = true
    targetColumnID = columnID
    targetColumnIDs = resizeTargets(columnID, context.areaHelper, context.groups)

    startPositionX = e.pageX
    columnWidth.guideX = e.pageX
}

/** 가이드선을 따라 움직인다. 최소 너비보다 좁아지는 자리에서는 더 들어오지 않는다 */
export function moveColumnResize(e: MouseEvent): void {
    let xPosition = e.pageX
    const width = widthOf(targetColumnID)
    const targetWidth = width - (startPositionX - xPosition)
    const minimum = store.state.V2ReservationCellConfig.defaultMinimumWidth
    if (targetWidth < minimum) {
        xPosition = -width + startPositionX + minimum
    }

    columnWidth.guideX = xPosition
}

/** 놓는 순간 대상 열 전부에 같은 너비를 확정한다 */
export function endColumnResize(): void {
    columnWidth.isResizing = false

    const targetWidth = widthOf(targetColumnID) - (startPositionX - columnWidth.guideX)
    store.commit('MU_V2ReservationCellWidth', {
        hospitalID: currentHospitalID(),
        columns: targetColumnIDs.map((id) => ({ _id: id, width: targetWidth })),
    })

    columnWidth.storedWidths = storedWidthsOf()
}
